// This file was copied from https://github.com/DataDog/rum-react-integration-examples/tree/master/src/ErrorBoundary
// and slightly tweaked for our codebase.
import type { ReactNode } from 'react';
import { Component, isValidElement } from 'react';

import { getGlobalObject } from '@jane/business-admin/util';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DD_RUM: any;
  }
}

type FallbackRenderer = (error: Error) => ReactNode;

interface RumErrorBoundaryState {
  error?: Error;
  hasError: boolean;
  prevScope: string;
}

interface RumErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode | FallbackRenderer;
  scope: string;
}

/**
 * RumErrorBoundary component sends enriched errors to RUM.
 */
export class RumErrorBoundary extends Component<
  RumErrorBoundaryProps,
  RumErrorBoundaryState
> {
  static defaultProps = {
    scope: 'error-boundary',
  };

  constructor(props: RumErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, prevScope: props.scope };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(
    props: RumErrorBoundaryProps,
    state: RumErrorBoundaryState
  ) {
    if (state.prevScope !== props.scope) {
      return {
        hasError: false,
        error: undefined,
        prevScope: props.scope,
      };
    }

    return state;
  }

  override componentDidCatch(error: Error) {
    const RumGlobal = getGlobalObject<Window>().DD_RUM;
    if (RumGlobal) {
      RumGlobal.addError(
        error,
        {
          scope: this.props.scope,
        },
        'source'
      );
    }
  }

  override render() {
    const { hasError, error } = this.state;
    const { fallback } = this.props;

    if (!hasError || !error) {
      return this.props.children;
    }

    if (isValidElement(fallback) || typeof fallback === 'string') {
      return fallback;
    } else if (typeof fallback === 'function') {
      return fallback(error);
    }

    return null;
  }
}
